import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import TicketSelector from '../components/customer/TicketSelector';
import OrderForm from '../components/customer/OrderForm';
import OrderSummary from '../components/customer/OrderSummary';
import { db } from '../services/supabase';
import toast from 'react-hot-toast';
import { normalizeOrder, hasQrCode, calculateOrderPricing } from '../utils/order';
import BullseyeIcon from '../components/common/icons/BullseyeIcon';
import NoteIcon from '../components/common/icons/NoteIcon';
import ArrowRightIcon from '../components/common/icons/ArrowRightIcon';
import ArrowLeftIcon from '../components/common/icons/ArrowLeftIcon';
import TheaterMasksIcon from '../components/common/icons/TheaterMasksIcon';
import EmailIcon from '../components/common/icons/EmailIcon';
import GlobeIcon from '../components/common/icons/GlobeIcon';

const CustomerPage = () => {
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [step, setStep] = useState(1); // 1: Select, 2: Form, 3: Summary
  const [orderData, setOrderData] = useState(null);
  const queryClient = useQueryClient();
  const { data: eventSettings } = useQuery({
    queryKey: ['event-settings'],
    queryFn: db.eventSettings.getLatest,
    staleTime: 5 * 60 * 1000,
  });
  const ticketPrice = Number(eventSettings?.ticket_price ?? 5);
  const selectionPricing = calculateOrderPricing(selectedTickets.length, ticketPrice);
  const sessionToken = useMemo(() => {
    const storageKey = 'tombola_session_token';
    const existing = window.localStorage.getItem(storageKey);
    if (existing) return existing;

    const generated =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    window.localStorage.setItem(storageKey, generated);
    return generated;
  }, []);

  // Create order mutation
  const createOrderMutation = useMutation({
    mutationFn: (data) => db.orders.create({
      first_name: data.first_name,
      last_name: data.last_name,
      email: data.email,
      tickets: selectedTickets,
      session_token: sessionToken,
    }),
    onSuccess: async (order) => {
      const normalizedOrder = normalizeOrder(order);

      setOrderData(normalizedOrder);
      setStep(3);
      
      // Okamžite invalidujeme a obnovíme lístky, aby sa zobrazili ako rezervované
      await queryClient.invalidateQueries({ queryKey: ['tickets'] });
      await queryClient.refetchQueries({ queryKey: ['tickets'] });
      
      // Send notification to Discord
      if (hasQrCode(normalizedOrder)) {
        db.notifications.sendDiscord({
          type: 'new_order',
          order: normalizedOrder
        }).catch(console.error);
      }
    },
    onError: (error) => {
      toast.error(`Chyba pri vytváraní objednávky: ${error.message}`);
    }
  });

  const handleFormSubmit = (formData) => {
    createOrderMutation.mutate(formData);
  };

  const resetOrder = () => {
    Promise.resolve(db.tickets.releaseHolds(sessionToken)).catch(console.error);
    setSelectedTickets([]);
    setStep(1);
    setOrderData(null);
  };

  useEffect(() => {
    if (step === 3 || selectedTickets.length === 0) return;

    const intervalId = setInterval(() => {
      db.tickets.syncHolds(sessionToken, selectedTickets, 60).catch(() => {});
    }, 20000);

    return () => clearInterval(intervalId);
  }, [sessionToken, selectedTickets, step]);

  useEffect(() => {
    return () => {
      Promise.resolve(db.tickets.releaseHolds(sessionToken)).catch(() => {});
    };
  }, [sessionToken]);

  const nextStep = () => {
    if (step === 1 && selectedTickets.length === 0) {
      toast.error('Vyberte aspoň jeden lístok');
      return;
    }
    setStep(step + 1);
  };

  const prevStep = () => {
    setStep(step - 1);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 text-slate-100">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl md:text-6xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-primary-400 via-primary-500 to-primary-300">
            <span className="inline-flex items-center justify-center gap-3">
              <BullseyeIcon className="text-5xl text-primary-300 drop-shadow-lg" aria-hidden="true" />
              <span>Tombola 2026</span>
            </span>
          </h1>
          <p className="text-xl text-slate-300 mb-6">
            Slovenský zväz posunkujúcich - Ples 2026
          </p>
          
          {/* Progress Bar */}
          <div className="max-w-md mx-auto mb-8">
            <div className="flex items-center justify-between mb-2">
              {[1, 2, 3].map((stepNumber) => (
                <div
                  key={stepNumber}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    step >= stepNumber
                      ? 'bg-primary-500 text-slate-950 shadow-glow'
                      : 'bg-slate-800 text-slate-500'
                  }`}
                >
                  {stepNumber}
                </div>
              ))}
            </div>
            <div className="w-full bg-slate-800 rounded-full h-2">
              <div
                className="bg-primary-500 h-2 rounded-full transition-all duration-500"
                style={{ width: `${((step - 1) / 2) * 100}%` }}
              ></div>
            </div>
            <div className="flex justify-between text-xs text-slate-400 mt-1">
              <span>Výber lístkov</span>
              <span>Údaje</span>
              <span>Dokončenie</span>
            </div>
          </div>
        </motion.div>

        {/* Step Content */}
        <div className="max-w-6xl mx-auto">
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              className="space-y-6 pb-24 md:pb-6"
            >
              <TicketSelector
                selectedTickets={selectedTickets}
                onTicketsChange={setSelectedTickets}
                sessionToken={sessionToken}
                ticketPrice={ticketPrice}
              />
              
              {/* Navigation - Fixed on mobile, normal on desktop */}
              <div className="fixed bottom-4 right-4 md:relative md:bottom-auto md:right-auto md:flex md:justify-end z-[9999]">
                <button
                  onClick={nextStep}
                  disabled={selectedTickets.length === 0}
                  className="text-lg px-8 py-4 rounded-xl font-bold transition-all duration-200 disabled:cursor-not-allowed inline-flex items-center gap-2 shadow-2xl hover:shadow-2xl focus:outline-none focus:ring-4 focus:ring-primary-400 focus:ring-offset-2 focus:ring-offset-slate-950 border-2 border-primary-600"
                  style={{ 
                    backgroundColor: selectedTickets.length === 0 ? '#6b7280' : '#d3ae59',
                    color: '#0f172a',
                    opacity: 1
                  }}
                >
                  <span>Pokračovať</span>
                  <ArrowRightIcon aria-hidden="true" />
                </button>
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              className="grid grid-cols-1 lg:grid-cols-3 gap-8"
            >
              <div className="lg:col-span-2">
                <OrderForm
                  onSubmit={handleFormSubmit}
                  isLoading={createOrderMutation.isPending}
                  selectedTickets={selectedTickets}
                  ticketPrice={ticketPrice}
                />
              </div>
              
              <div>
                {/* Selected Tickets Summary */}
                <div className="card sticky top-6 backdrop-blur-sm">
                  <h3 className="text-lg font-semibold text-primary-100 mb-4 flex items-center gap-2">
                    <NoteIcon className="text-primary-300" aria-hidden="true" />
                    <span>Súhrn výberu</span>
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <div className="text-sm text-slate-400 mb-2">Vybrané lístky:</div>
                      <div className="flex flex-wrap gap-2">
                        {selectedTickets.slice(0, 10).map(ticket => (
                          <span 
                            key={ticket}
                            className="bg-primary-500/15 text-primary-100 px-2 py-1 rounded text-sm font-medium border border-primary-500/20"
                          >
                            {ticket}
                          </span>
                        ))}
                        {selectedTickets.length > 10 && (
                          <span className="bg-slate-800 text-slate-400 px-2 py-1 rounded text-sm border border-slate-700">
                            +{selectedTickets.length - 10}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="border-t border-slate-800 pt-4">
                      <div className="flex justify-between text-lg font-bold">
                        <span>Celkom:</span>
                        <span className="text-primary-200">{selectionPricing.totalAmount}€</span>
                      </div>
                      {selectionPricing.freeTickets > 0 && (
                        <p className="text-sm text-success-200 mt-2">
                          Započítané zdarma: {selectionPricing.freeTickets} lístok
                          {selectionPricing.freeTickets === 1 ? '' : 'y'}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Navigation */}
              <div className="lg:col-span-3 flex justify-between">
                <button
                  onClick={prevStep}
                  className="btn btn-secondary inline-flex items-center gap-2"
                >
                  <ArrowLeftIcon aria-hidden="true" />
                  <span>Späť</span>
                </button>
              </div>
            </motion.div>
          )}

          {step === 3 && orderData && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="max-w-2xl mx-auto"
            >
              <OrderSummary
                selectedTickets={selectedTickets}
                totalAmount={orderData.totalAmount}
                ticketPrice={ticketPrice}
                customerData={{
                  firstName: orderData.firstName,
                  lastName: orderData.lastName,
                  email: orderData.email
                }}
                qrCode={orderData.qrCode}
                orderId={orderData.id}
              />
              
              <div className="mt-8 text-center">
                <button
                  onClick={resetOrder}
                  className="btn btn-secondary inline-flex items-center gap-2"
                >
                  <BullseyeIcon aria-hidden="true" />
                  <span>Nová objednávka</span>
                </button>
              </div>
            </motion.div>
          )}
        </div>

        {/* Footer */}
        <motion.footer
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="text-center mt-16 text-slate-500"
        >
          <div className="mb-4">
            <p className="text-sm">
              Deadline: 14. február 2026 o 23:00 | Cena lístka: {ticketPrice}€
            </p>
          </div>
          <div className="text-xs space-y-1">
            <p className="flex items-center justify-center gap-2">
              <TheaterMasksIcon aria-hidden="true" />
              <span>Slovenský zväz posunkujúcich</span>
            </p>
            <p className="flex items-center justify-center gap-2">
              <EmailIcon aria-hidden="true" />
              <span>aplikacia@posunkujuci.com</span>
            </p>
            <p className="flex items-center justify-center gap-2">
              <GlobeIcon aria-hidden="true" />
              <span>posunkujuci.com</span>
            </p>
          </div>
        </motion.footer>
      </div>

      {/* Loading Overlay */}
      {createOrderMutation.isPending && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 text-center shadow-lg">
            <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-primary-400 mx-auto mb-4"></div>
            <h3 className="text-lg font-semibold text-primary-100 mb-2">
              Vytváranie objednávky...
            </h3>
            <p className="text-slate-300">Prosím čakajte</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerPage;
